import { authSelectors } from 'ducks/auth'
import {
  addOrderLineItems,
  confirmLineItems,
  deleteLineItems,
  deleteOrderByCode,
  deleteOrderBySystemId,
  orderSelectors,
  updateLineItem,
  updateLoadedProductData,
  updateOrderQuantity,
} from 'ducks/orderComponents'
import { orgSelectors } from 'ducks/orgs'
import type { ComponentLikeType, DistributorDataTypeV2, HardwareComponentType } from 'elements/hardwareSelectorV2/types'
import moment from 'moment'
import type LineItemType from 'pages/ordering/OrderLineItem'
import { OrderLineItem } from 'pages/ordering/OrderLineItem'
import {
  type BasicComponentLineItemType,
  type BomLineItemType,
  type ComponentCost,
  type HardwareSupplierFilterKeyType,
  type LineItemLoadedDataType,
  type ProjectOrderDetail,
  type StockLevelStatus,
  type StockLevelType,
  type SystemDesignLineItemCategoryType,
  type SystemDesignType,
} from 'pages/ordering/type'
import type { LegacyDataProvider } from 'react-admin'
import type { Dispatch, Store } from 'redux'
import restClient from 'restClient'
import { InverterDataType } from 'types/inverter'
import type { ComponentTypes, ComponentTypesV2 } from 'types/selectComponent'
import type { RootState } from 'types/state'
import type { SystemDataType } from 'types/systems'
import { getComponentTitleFromData } from 'util/hardware'
import { STOCK_LEVEL_STATUS } from '../constants'

const CATEGORY_AND_PRODUCT_TYPE_MAP: Record<SystemDesignLineItemCategoryType, ComponentTypes> = {
  batteries: 'battery',
  inverters: 'inverter',
  modules: 'module',
  others: 'other',
}

const NON_DISTRIBUTOR_SOURCES = ['component', 'manual', 'unknown', 'overridden', 'exclude']

/**
 * Finds the distributor source for a component by looking up its code in component_overrides.
 * Returns undefined if no override is found for this component.
 */
const getDistributorFromOverrides = (
  code: string,
  componentOverrides?: ComponentCost[]
): HardwareSupplierFilterKeyType | undefined => {
  if (!componentOverrides || componentOverrides.length === 0) return undefined

  // These are special source values that don't correspond to actual distributors
  const nonDistributorSources = ['component', 'manual', 'unknown', 'overridden']

  const override = componentOverrides.find((o) => o.code === code)
  if (override?.source && !nonDistributorSources.includes(override.source)) {
    return override.source as HardwareSupplierFilterKeyType
  }
  return undefined
}

/**
 * Returns 'exclude' when the component override is Manual Cost (component/manual).
 * Used so the Select Source column displays "Exclude from order" by default for manual cost items.
 */
const getManualCostSourceFromOverrides = (
  code: string,
  componentOverrides?: ComponentCost[]
): 'exclude' | undefined => {
  if (!componentOverrides || componentOverrides.length === 0) return undefined

  const override = componentOverrides.find((o) => o.code === code)
  if (override?.source && ['component', 'manual'].includes(override.source)) {
    return 'exclude'
  }
  return undefined
}

const getInverterModuleQuantity = (inverter: InverterDataType): number => {
  return (
    (inverter.mppts &&
      inverter.mppts
        .map((mppt) => mppt.strings.reduce((sum, string) => sum + string.modules.length, 0))
        .reduce((sum, cur) => sum + cur, 0)) ||
    0
  )
}

const getTotalUnstrungModulesQuantity = (system: SystemDataType): number => {
  return system.unstrungModulesByModuleGrid
    ? system.unstrungModulesByModuleGrid.map((mg) => mg.modules.length).reduce((a, b) => a + b, 0)
    : 0
}

class ProjectOrderPresenter {
  private store
  dispatch
  restClientInstance

  constructor(store: Store<RootState>, dispatch: Dispatch, restClientInstance: LegacyDataProvider) {
    this.store = store
    this.dispatch = dispatch
    this.restClientInstance = restClientInstance
  }

  private getState = () => {
    return this.store.getState()
  }

  /** Sum of units across all line items. For bulk items, quantity is already in units (e.g. 36), so we sum quantity * quantityPerItem. */
  static getLineItemsTotalQuantity = (lineItems: LineItemType[]) => {
    let total = 0
    lineItems.forEach((item: LineItemType) => {
      const { quantity, quantityPerItem = 1 } = item
      if (quantity != null && quantityPerItem != null) {
        total += quantity * quantityPerItem
      }
    })
    return total
  }

  /** Merge projectOrder arrays by (projectId, systemId), summing requiredQuantity. */
  static mergeProjectOrderByAllocation = (
    a: ProjectOrderDetail[] | undefined,
    b: ProjectOrderDetail[] | undefined
  ): ProjectOrderDetail[] => {
    const combined = [...(a ?? []), ...(b ?? [])]
    const byKey = new Map<string, ProjectOrderDetail>()
    for (const po of combined) {
      const key = `${po.projectId}-${po.systemId ?? 'default'}`
      const existing = byKey.get(key)
      if (existing) {
        byKey.set(key, { ...existing, requiredQuantity: existing.requiredQuantity + po.requiredQuantity })
      } else {
        byKey.set(key, { projectId: po.projectId, systemId: po.systemId, requiredQuantity: po.requiredQuantity })
      }
    }
    return Array.from(byKey.values())
  }

  static aggregateLineItems = (lineItems: LineItemType[]) => {
    const aggregateLineItems: LineItemType[] = []
    function getHashKey(lineItem: LineItemType) {
      // Normalise quantityPerItem so "Exclude from Order" / BOM lines (often undefined) merge with existing cart lines (often 1)
      const qtyPerItem = lineItem.bulkOption?.quantity_per_item ?? lineItem.quantityPerItem ?? 1
      return `${lineItem.code}+${qtyPerItem}+${lineItem.selectedDistributor}+${lineItem.confirmed ? 'confirmed' : 'unconfirmed'}`
    }
    // <code+quantityPerItem, index>
    const hashMapHelper: Record<string, number> = {}
    lineItems.forEach((lineItem: LineItemType) => {
      const recordedIndex = hashMapHelper[getHashKey(lineItem)]
      if (recordedIndex == null) {
        // record index
        aggregateLineItems.push(lineItem)
        hashMapHelper[getHashKey(lineItem)] = aggregateLineItems.length - 1
        return
      }

      const existingRecord: LineItemType | undefined = aggregateLineItems[recordedIndex]

      if (existingRecord.bulkOption && lineItem.bulkOption) {
        existingRecord.quantity += lineItem.bulkQuantity! * existingRecord.bulkOption.quantity_per_item
      } else {
        existingRecord.quantity += lineItem.quantity
      }

      existingRecord.projectOrder = ProjectOrderPresenter.mergeProjectOrderByAllocation(
        existingRecord.projectOrder,
        lineItem.projectOrder
      )
    })

    return aggregateLineItems
  }

  static getPriceAndStock = async ({
    orgId,
    lineItems,
    supplierFilterKey,
  }: {
    orgId: number
    lineItems: LineItemType[]
    supplierFilterKey?: string
  }): Promise<LineItemLoadedDataType[] | undefined> => {
    const restClientInstance = restClient(window.API_ROOT + '/api')
    try {
      const response = await restClientInstance('CUSTOM_GET', 'components', {
        url: `components/?org_id=${orgId}&catalog=full&require_distributor=${supplierFilterKey}&filter_codes=${lineItems
          .map((lineItems) => lineItems.code)
          .join(',')}`,
      })
      return response.data
    } catch (error) {
      console.log('Event not recorded', error)
    }
  }

  static isStockLevelReadyToOrder = (stockLevelStatus: StockLevelStatus) =>
    stockLevelStatus === 'loading' || stockLevelStatus === 'available' || stockLevelStatus === 'low_stock'

  static getStockPlannedDateDays = (stockLevels: StockLevelType[] | undefined): number => {
    const stockLevelsPlanned: StockLevelType | undefined = stockLevels?.find(
      (stockLevel: StockLevelType) => !stockLevel.in_stock && stockLevel.planned_date
    )
    if (!stockLevelsPlanned) {
      return 0
    }
    const date = stockLevelsPlanned.planned_date
    return moment(date).diff(moment(), 'days')
  }

  static getStockLevelStatusMain = ({
    quantity,
    stockLevels,
    isAvailable,
  }: {
    quantity: number
    stockLevels: StockLevelType[] | undefined
    isAvailable: boolean
  }): StockLevelStatus => {
    const selectedHardwareSupplier = orgSelectors.getSelectedHardwareSupplier(window.reduxStore?.getState())
    if (!isAvailable) {
      return STOCK_LEVEL_STATUS.NOT_AVAILABLE as StockLevelStatus
    }

    const inStockLevel = stockLevels?.find((stock: StockLevelType) => stock.in_stock)

    if (!inStockLevel) {
      // available for pre order
      // applicable for solar outlet only
      if (selectedHardwareSupplier === 'outlet') {
        const stockLevelsPlanned: StockLevelType[] | undefined = stockLevels?.filter(
          (stockLevel: StockLevelType) => !stockLevel.in_stock && stockLevel.planned_date
        )
        if (stockLevelsPlanned != null) {
          return STOCK_LEVEL_STATUS.AVAILABLE_FOR_PRE_ORDER as StockLevelStatus
        }
      }
      return 'out_of_stock'
    } else {
      if (isNaN(quantity)) {
        return STOCK_LEVEL_STATUS.AVAILABLE as StockLevelStatus
      }
    }

    if (inStockLevel.quantity >= quantity) {
      if (inStockLevel.quantity > quantity * 1.25) {
        return STOCK_LEVEL_STATUS.AVAILABLE as StockLevelStatus
      } else {
        return STOCK_LEVEL_STATUS.LOW_STOCK as StockLevelStatus
      }
    }
    if (inStockLevel.quantity < quantity) {
      return STOCK_LEVEL_STATUS.PARTIAL_AVAILABLE as StockLevelStatus
    }

    // Set default stock level status to not available?
    return STOCK_LEVEL_STATUS.AVAILABLE_FOR_PRE_ORDER as StockLevelStatus
  }

  /// Created this as a standalone so we could refactor for v3.0 without breaking the existing code
  static getStockLevelStatusMainV3 = ({
    distributor,
    quantity,
    stockLevels,
    isAvailable,
    isInStock,
    preferredStockLocation,
  }: {
    distributor: HardwareSupplierFilterKeyType
    quantity: number // requested
    stockLevels: StockLevelType[] | undefined
    isAvailable: boolean
    isInStock?: boolean
    preferredStockLocation: string | null
  }): StockLevelStatus => {
    if (!isAvailable) {
      return STOCK_LEVEL_STATUS.NOT_AVAILABLE as StockLevelStatus
    }

    const preferredStockLevel = stockLevels?.find(
      ({ location }: StockLevelType) =>
        (location?.key ?? null) === preferredStockLocation && (location?.is_active ?? true)
    )
    const inStockLevel =
      preferredStockLevel ??
      stockLevels?.find(({ in_stock, location }: StockLevelType) => in_stock && location === null)

    if (!inStockLevel) {
      // available for pre order
      // applicable for solar outlet only
      if (['outlet'].includes(distributor)) {
        const stockLevelsPlanned: StockLevelType[] | undefined = stockLevels?.filter(
          (stockLevel: StockLevelType) => !stockLevel.in_stock && stockLevel.planned_date
        )
        if (stockLevelsPlanned != null) {
          return STOCK_LEVEL_STATUS.AVAILABLE_FOR_PRE_ORDER as StockLevelStatus
        }
      }
      if (!stockLevels?.length) {
        if (isInStock === true) {
          return STOCK_LEVEL_STATUS.AVAILABLE as StockLevelStatus
        }
        if (isInStock === false) {
          return STOCK_LEVEL_STATUS.OUT_OF_STOCK as StockLevelStatus
        }
      }
      return STOCK_LEVEL_STATUS.OUT_OF_STOCK as StockLevelStatus
    } else {
      if (isNaN(quantity)) {
        return STOCK_LEVEL_STATUS.AVAILABLE as StockLevelStatus
      }
    }

    if (inStockLevel.quantity === 0) {
      return STOCK_LEVEL_STATUS.OUT_OF_STOCK as StockLevelStatus
    }

    if (inStockLevel.quantity >= quantity) {
      if (inStockLevel.quantity > quantity * 1.25) {
        return STOCK_LEVEL_STATUS.AVAILABLE as StockLevelStatus
      } else {
        return STOCK_LEVEL_STATUS.LOW_STOCK as StockLevelStatus
      }
    }
    if (inStockLevel.quantity < quantity) {
      return STOCK_LEVEL_STATUS.PARTIAL_AVAILABLE as StockLevelStatus
    }

    // Set default stock level status to not available?
    return STOCK_LEVEL_STATUS.AVAILABLE_FOR_PRE_ORDER as StockLevelStatus
  }

  static getStockLevelStatusFromLineItem = (lineItem: LineItemType): StockLevelStatus => {
    if (lineItem.status === 'loading') {
      return 'loading'
    }
    const { quantity, stockLevels, pricePerUnit, selectedDistributorOrderingData } = lineItem

    return ProjectOrderPresenter.getStockLevelStatusMain({
      quantity,
      stockLevels,
      isAvailable: !!selectedDistributorOrderingData?.is_available,
    })
  }

  static getLineItemByLoadedData = (
    lineItem: LineItemType,
    loadedData: LineItemLoadedDataType | undefined
  ): LineItemType => {
    // Find the distributor data matching the line item's selected distributor
    const distributorData = loadedData?.distributors?.find((d) => d.distributor === lineItem.selectedDistributor)

    const newLineItem = new OrderLineItem({
      ...lineItem,
      status: 'loaded',
      data: loadedData,
      componentType: loadedData?.component_type || lineItem.componentType,
      // Set pricing data from the matching distributor
      ...(distributorData && {
        selectedDistributorOrderingData: distributorData,
        pricePerUnit: distributorData.price,
        quantityPerItem: distributorData.quantity_per_item,
        stockLevels: distributorData.stock_levels,
        variantId: distributorData.variant_id,
        variantDiscounts: distributorData.volume_discounts,
      }),
    })

    return newLineItem
  }

  private injectVariantIds = async (lineItems: LineItemType[]): Promise<LineItemLoadedDataType[] | undefined> => {
    try {
      const response = await this.restClientInstance('CUSTOM_POST', 'inject_variant_ids', {
        url: 'inject_variant_ids/',
        data: lineItems,
      })
      return response.data
    } catch (error) {
      console.log('Event not recorded', error)
    }
  }

  getLineItemsBySystemId = (systemId: number) => {
    const state = this.getState()
    return orderSelectors.getLineItemsBySystemId(state, systemId)
  }

  deleteUnconfirmedLineItemsBySystemId = (systemId: number) => {
    this.dispatch(deleteOrderBySystemId({ systemId, confirmed: false }))
  }

  /** Remove all line items for a system (confirmed and unconfirmed). Used when unchecking a project on MPO. */
  deleteLineItemsBySystemId = (systemId: number) => {
    this.dispatch(deleteOrderBySystemId({ systemId, confirmed: false }))
    this.dispatch(deleteOrderBySystemId({ systemId, confirmed: true }))
  }

  deleteLineItemByUuid = (uuid: string) => {
    this.dispatch(deleteLineItems<'uuid'>({ source: 'uuid', identifiers: [uuid] }))
  }

  deleteLineItemByUuids = (uuids: string[]) => {
    this.dispatch(deleteLineItems<'uuid'>({ source: 'uuid', identifiers: uuids }))
  }

  deleteLineItemsByCode = (code: string) => {
    this.dispatch(deleteOrderByCode(code))
  }

  addLineItemsLegacy = async (lineItems: LineItemType[]) => {
    this.dispatch(addOrderLineItems(lineItems))
    const response = await this.injectVariantIds(lineItems)
    if (response == null) {
      return
    }
    this.dispatch(updateLoadedProductData(response, lineItems))
  }

  addLineItems = async (lineItems: LineItemType[]) => {
    this.dispatch(addOrderLineItems(lineItems))
  }

  /** Add line items and immediately confirm them (e.g. for MPO so they appear in global cart). */
  addLineItemsAndConfirm = (lineItems: LineItemType[]) => {
    this.dispatch(addOrderLineItems(lineItems))
    this.dispatch(
      confirmLineItems({
        uuids: lineItems.map((li) => li.uuid),
        confirmed: true,
      })
    )
  }

  updateLineItemQuantity = ({ uuid, quantity }: { uuid: string; quantity: number }) => {
    this.dispatch(updateOrderQuantity({ uuid, quantity }))
  }

  /**
   * Sets total quantity for a component code (Review Cart). Preserves project allocations; any increase
   * is stored as extra quantity on the line item (unallocated to projects).
   * Handles both increases and decreases so the consolidated total matches the user's input.
   * Uses confirmed line items so updates apply to the items shown on the Cart page.
   */
  setTotalQuantityByCode = (code: string, newTotal: number) => {
    const state = this.getState() as RootState
    const lineItemsWithCode = orderSelectors.getLineItemsByCode(state, code).filter((li) => li.confirmed)
    const withAllocation = lineItemsWithCode.filter((li) => li.projectOrder?.length)
    const allocatedSum = withAllocation.reduce((s, li) => s + li.quantity, 0)

    const first = withAllocation[0] || lineItemsWithCode[0]
    if (!first) return

    if (newTotal > allocatedSum) {
      this.dispatch(updateOrderQuantity({ uuid: first.uuid, quantity: newTotal }))
    } else if (newTotal >= 0) {
      // When decreasing: set first to newTotal, zero out others so consolidated sum = newTotal
      this.dispatch(updateOrderQuantity({ uuid: first.uuid, quantity: newTotal }))
      lineItemsWithCode.forEach((li) => {
        if (li.uuid !== first.uuid) {
          this.dispatch(updateOrderQuantity({ uuid: li.uuid, quantity: 0 }))
        }
      })
    }
  }

  updateLineItemDistributor = ({
    uuid,
    distributor,
  }: {
    uuid: string
    distributor: HardwareSupplierFilterKeyType | undefined
  }) => {
    this.dispatch({
      type: 'UPDATE_LINE_ITEM',
      payload: {
        uuid,
        source: 'selectedDistributor',
        value: distributor,
      },
    })
  }

  /** Updates selectedDistributor for ALL line items with the given code. Use for Review BOM consolidated rows. */
  updateLineItemDistributorByCode = ({
    code,
    distributor,
  }: {
    code: string
    distributor: HardwareSupplierFilterKeyType | undefined
  }) => {
    const state = this.getState()
    const lineItemsWithCode = orderSelectors.getLineItemsByCode(state, code)
    lineItemsWithCode.forEach((li) => {
      this.dispatch(updateLineItem({ uuid: li.uuid, source: 'selectedDistributor', value: distributor }))
    })
  }

  // To make it consistent, move this logic to backend
  getLineItemsFromSystem = (soldSystemData: SystemDataType, selectedDistributor: HardwareSupplierFilterKeyType) => {
    if (!soldSystemData) {
      alert('Sorry, this action is currently unavailable')
      return
    }
    const lineItems: LineItemType[] = []
    lineItems.push(
      new OrderLineItem({
        componentType: 'module',
        status: 'loading',
        code: soldSystemData.module.code,
        quantity: soldSystemData.module_quantity,
        quantityPerItem: 1,
        selectedDistributor,
      })
    )
    soldSystemData.inverters.forEach((inverter) => {
      const inverterQuantity = inverter.microinverter
        ? getInverterModuleQuantity(inverter) || getTotalUnstrungModulesQuantity(soldSystemData)
        : 1
      const indexOfLineItems = lineItems.findIndex((i) => i.code === inverter.code)
      if (indexOfLineItems !== -1) {
        lineItems[indexOfLineItems].quantity += inverterQuantity
      } else {
        lineItems.push(
          new OrderLineItem({
            componentType: 'inverter',
            status: 'loading',
            code: inverter.code,
            quantity: inverterQuantity,
            quantityPerItem: 1,
            selectedDistributor,
          })
        )
      }
    })
    soldSystemData.batteries.forEach((battery) => {
      const indexOfLineItems = lineItems.findIndex((i) => i.code === battery.code)
      if (indexOfLineItems !== -1) {
        lineItems[indexOfLineItems].quantity = lineItems[indexOfLineItems].quantity ?? 0 + 1
      } else {
        lineItems.push(
          new OrderLineItem({
            componentType: 'battery',
            status: 'loading',
            code: battery.code,
            quantity: 1,
            quantityPerItem: 1,
            selectedDistributor,
          })
        )
      }
    })
    soldSystemData.others.forEach((other) => {
      const indexOfLineItems = lineItems.findIndex((i) => i.code === other.code)
      if (indexOfLineItems !== -1) {
        lineItems[indexOfLineItems].quantity += other.quantity
      } else {
        lineItems.push(
          new OrderLineItem({
            componentType: 'other',
            status: 'loading',
            code: other.code,
            quantity: other.quantity,
            quantityPerItem: 1,
            selectedDistributor,
          })
        )
      }
    })
    //To Do: get correct quantity
    return lineItems
  }

  getLineItemsFromSystemDesignData = ({
    systemDesign,
    projectId,
    systemId,
  }: {
    systemDesign: SystemDesignType
    projectId: number
    systemId: number
  }): LineItemType[] => {
    const state = this.getState()
    const orgId = authSelectors.getOrgId(state)

    const categories: SystemDesignLineItemCategoryType[] = ['batteries', 'inverters', 'modules', 'others']
    const lineItems: LineItemType[] = []
    categories.forEach((category: SystemDesignLineItemCategoryType) => {
      const categoryItems = systemDesign[category]
      if (!categoryItems || categoryItems.length === 0) return
      const newLineItems: LineItemType[] = categoryItems.map(
        (basicLineItem: BasicComponentLineItemType) =>
          new OrderLineItem({
            ...basicLineItem,
            status: 'loading',
            quantityPerItem: 1,
            componentType: CATEGORY_AND_PRODUCT_TYPE_MAP[category],
            orgId,
            projectOrder: [
              {
                projectId,
                systemId,
                requiredQuantity: basicLineItem.quantity,
              },
            ],
            selectedDistributor: getDistributorFromOverrides(basicLineItem.code, systemDesign.component_overrides),
          })
      )
      lineItems.push(...newLineItems)
    })
    return lineItems
  }

  /**
   * Creates fully-loaded line items using pre-fetched componentsv2 data.
   * This bypasses the saga since items are created with status: 'loaded'.
   *
   * Merges items from both bom_line_items (saved BOM) and system design component arrays.
   * bom_line_items may contain manually added items not in system design.
   * Component arrays may contain items not yet saved to bom_line_items.
   */
  getLineItemsFromSystemDesignDataWithComponents = ({
    systemDesign,
    projectId,
    systemId,
    componentMap,
  }: {
    systemDesign: SystemDesignType
    projectId: number
    systemId: number
    componentMap: Record<string, ComponentLikeType>
  }): LineItemType[] => {
    const state = this.getState()
    const orgId = authSelectors.getOrgId(state)
    const lineItems: LineItemType[] = []
    const processedCodes = new Set<string>()

    const bomLineItems = systemDesign?.bom_line_items
    const bomItemsArray: BomLineItemType[] = Array.isArray(bomLineItems)
      ? bomLineItems
      : bomLineItems && typeof bomLineItems === 'object'
        ? (Object.values(bomLineItems) as BomLineItemType[])
        : []

    if (bomItemsArray.length > 0) {
      bomItemsArray.forEach((bomItem) => {
        if (!bomItem?.code) return
        const componentData = componentMap[bomItem.code] as HardwareComponentType
        const orderingData = componentData?.ordering_v2
        const isExcludedFromOrder =
          bomItem.source && ['component', 'manual', 'exclude', 'unknown'].includes(bomItem.source)

        // Include excluded-from-order components in the BOM for visibility, even without ordering data
        if (!componentData && !isExcludedFromOrder && (!orderingData || orderingData.length === 0)) {
          return
        }

        // Dedupe: if same code appears twice in bom_line_items (e.g. "Empowered by Light"), merge quantity into existing line item
        if (processedCodes.has(bomItem.code)) {
          const existing = lineItems.find((li) => li.code === bomItem.code)
          if (existing && existing.projectOrder?.[0]) {
            existing.quantity += bomItem.quantity
            existing.projectOrder[0].requiredQuantity += bomItem.quantity
          }
          return
        }
        processedCodes.add(bomItem.code)

        let selectedDistributor: HardwareSupplierFilterKeyType | 'exclude' | undefined = undefined
        let distributorData: DistributorDataTypeV2 | undefined

        if (!orderingData || orderingData.length === 0) {
          selectedDistributor = 'exclude'
          distributorData = undefined
        } else if (bomItem.source && ['component', 'manual', 'exclude', 'unknown'].includes(bomItem.source)) {
          selectedDistributor = 'exclude'
          distributorData = undefined
        } else if (bomItem.source && !NON_DISTRIBUTOR_SOURCES.includes(bomItem.source)) {
          selectedDistributor = bomItem.source as HardwareSupplierFilterKeyType
          distributorData = orderingData?.find((d) => d.distributor === selectedDistributor)
        }

        if (!distributorData && orderingData?.length && selectedDistributor !== 'exclude') {
          distributorData = orderingData[0]
          selectedDistributor = distributorData.distributor as HardwareSupplierFilterKeyType
        }

        // Set projectOrder when adding to cart from BOM so allocations and requiredQuantity are tracked for comparison and delete-by-systemId.
        const lineItem = new OrderLineItem({
          code: bomItem.code,
          quantity: bomItem.quantity,
          status: 'loaded',
          componentType: bomItem.componentType as ComponentTypes,
          componentTypeV2: (componentData?.type ?? bomItem.componentType) as ComponentTypesV2,
          orgId,
          projectOrder: [
            {
              projectId,
              systemId,
              requiredQuantity: bomItem.quantity,
            },
          ],
          orderingData: orderingData ?? [],
          selectedDistributor,
          selectedDistributorOrderingData: distributorData,
          pricePerUnit: distributorData?.price_detail?.price_ex_vat ?? distributorData?.price,
          stockLevels: distributorData?.stock_levels,
          variantId: distributorData?.variant_id,
          variantDiscounts: distributorData?.volume_discounts,
          quantityPerItem: 1,
          rating: componentData?.rating,
          componentTitle: getComponentTitleFromData(componentData?.data),
        })

        lineItems.push(lineItem)
      })
    }

    const categories: SystemDesignLineItemCategoryType[] = ['batteries', 'inverters', 'modules', 'others']

    categories.forEach((category: SystemDesignLineItemCategoryType) => {
      systemDesign[category]?.forEach((basicLineItem: BasicComponentLineItemType) => {
        const componentData = componentMap[basicLineItem.code] as HardwareComponentType | undefined
        const orderingData = componentData?.ordering_v2

        // Dedupe: same code may already exist from bom_line_items (e.g. "Empowered by Light" in both). Skip so we don't add a second line item.
        if (processedCodes.has(basicLineItem.code)) {
          return
        }

        // Determine selected distributor from component overrides first (before skip check)
        const manualCostSource = getManualCostSourceFromOverrides(basicLineItem.code, systemDesign.component_overrides)
        const isExcludedFromOrder = manualCostSource === 'exclude'

        // Include excluded-from-order components in the BOM for visibility, even without ordering data
        if (!componentData && !isExcludedFromOrder && (!orderingData || orderingData.length === 0)) {
          return
        }

        processedCodes.add(basicLineItem.code)

        // Determine selected distributor from component overrides
        // Manual Cost (component/manual): display "Exclude from order" by default
        let selectedDistributor: HardwareSupplierFilterKeyType | 'exclude' | undefined =
          getManualCostSourceFromOverrides(basicLineItem.code, systemDesign.component_overrides) ??
          getDistributorFromOverrides(basicLineItem.code, systemDesign.component_overrides)

        let distributorData: DistributorDataTypeV2 | undefined = orderingData?.find(
          (d) => d.distributor === selectedDistributor
        )

        if (!orderingData || orderingData.length === 0) {
          selectedDistributor = 'exclude'
          distributorData = undefined
        } else if (!distributorData && selectedDistributor !== 'exclude') {
          distributorData = orderingData[0]
          selectedDistributor = distributorData.distributor as HardwareSupplierFilterKeyType
        }

        const lineItem = new OrderLineItem({
          code: basicLineItem.code,
          quantity: basicLineItem.quantity,
          status: 'loaded', // Skip saga - data already loaded
          componentType: CATEGORY_AND_PRODUCT_TYPE_MAP[category],
          componentTypeV2: (componentData?.type ?? CATEGORY_AND_PRODUCT_TYPE_MAP[category]) as ComponentTypesV2,
          orgId,
          projectOrder: [
            {
              projectId,
              systemId,
              requiredQuantity: basicLineItem.quantity,
            },
          ],
          // Full ordering data for all distributors (empty for excluded items without catalog)
          orderingData: orderingData ?? [],
          // Selected distributor info
          selectedDistributor,
          selectedDistributorOrderingData: distributorData,
          // Pricing and stock from selected distributor
          pricePerUnit: distributorData?.price_detail?.price_ex_vat ?? distributorData?.price,
          stockLevels: distributorData?.stock_levels,
          variantId: distributorData?.variant_id,
          variantDiscounts: distributorData?.volume_discounts,
          quantityPerItem: 1,
          rating: componentData?.rating,
          componentTitle: getComponentTitleFromData(componentData?.data),
        })

        lineItems.push(lineItem)
      })
    })

    return lineItems
  }
}

export default ProjectOrderPresenter
